import {Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoDirective} from '@jsverse/transloco';
import {LucideAngularModule, MailCheckIcon, ScanEyeIcon, SendHorizonalIcon, ShieldCheckIcon} from 'lucide-angular';
import {take} from 'rxjs';
import {HostDTO, HostSendMessageRequest} from '@domain/host/host.dto';
import {AltchaComponent} from '@shared/altcha.component';
import {PublicHostApi} from '@domain/host/public-host.api';
import {
  HostContactPrivacyNote
} from '@domain/host/component/host-contact-privacy-note/host-contact-privacy-note.component';
import {EmailFieldComponent} from '@shared/form/string/email-field/email-field.component';
import {StringFieldComponent} from '@shared/form/string/string-field/string-field.component';
import {TextFieldComponent} from '@shared/form/string/text-field/text-field.component';

@Component({
  selector: 'app-host-contact-form',
  providers: [],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    LucideAngularModule,
    AltchaComponent,
    HostContactPrivacyNote,
    EmailFieldComponent,
    StringFieldComponent,
    TextFieldComponent
  ],
  templateUrl: './host-contact-form.component.html'
})
export class HostContactForm implements OnInit {

  public host = input.required<HostDTO>();
  public placeholder = input<Partial<HostSendMessageRequest> | null>(null);
  public readonly isPreview = input(false);
  public readonly cancelled = output<void>();
  public readonly sent = output<void>();

  public readonly icons = {
    shield: ShieldCheckIcon,
    send: SendHorizonalIcon,
    sent: MailCheckIcon,
    preview: ScanEyeIcon
  };
  public readonly messageSent = signal(false);

  public readonly hostName = computed(() => this.host().name ?? '');
  private readonly publicHostApi = inject(PublicHostApi);
  private readonly fb = inject(FormBuilder);

  public form: FormGroup<{
    email: FormControl<string>;
    name: FormControl<string>;
    phone: FormControl<string | null>;
    subject: FormControl<string | null>;
    message: FormControl<string>;
    captcha: FormControl<string>
  }>;

  public ngOnInit(): void {
    const placeholder = this.placeholder();
    this.form = this.fb.group({
      email: this.fb.nonNullable.control<string>(placeholder?.senderEmail ?? '', {validators: [Validators.required, Validators.email]}),
      name: this.fb.nonNullable.control<string>(placeholder?.senderName ?? '', {validators: [Validators.required, Validators.maxLength(200)]}),
      phone: this.fb.control<string | null>(placeholder?.senderPhone ?? null, Validators.maxLength(200)),
      subject: this.fb.control<string | null>(placeholder?.subject ?? null, Validators.maxLength(255)),
      message: this.fb.nonNullable.control<string>(placeholder?.message ?? '', {validators: [Validators.required, Validators.maxLength(1000)]}),
      captcha: this.fb.nonNullable.control<string>('', Validators.required)
    });
  }

  public sendMessage(): void {
    if (!this.form.valid) {
      return;
    }
    const formData = this.form.value;
    const messageRequest: HostSendMessageRequest = {
      captcha: formData.captcha!,
      senderEmail: formData.email!,
      senderName: formData.name!,
      senderPhone: formData.phone ?? null,
      subject: formData.subject ?? null,
      message: formData.message!
    };
    this.publicHostApi.sendMessage(this.host().id, messageRequest).pipe(
      take(1)
    ).subscribe(() => {
      this.messageSent.set(true);
      this.sent.emit();
    });
  }

  public cancel(): void {
    this.cancelled.emit();
  }
}
